import { useRef, useEffect } from 'react';
import './HeroSection.css';
import logo from '../assets/logo-dzeltens.png';

const HeroSection = () => {
  const videoRef = useRef(null);

  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;

    // Ensure video plays after it's loaded
    const handleCanPlay = () => {
      video.play().catch(() => {
        // Autoplay was prevented, try again on user interaction
      });
    };

    // Handle visibility change - pause when tab is hidden, play when visible
    const handleVisibilityChange = () => {
      if (document.hidden) {
        video.pause();
      } else {
        video.play().catch(() => {});
      }
    };

    // Try to play on first user interaction (for mobile)
    const handleFirstInteraction = () => {
      video.play().catch(() => {});
      document.removeEventListener('touchstart', handleFirstInteraction);
      document.removeEventListener('click', handleFirstInteraction);
    };

    video.addEventListener('canplay', handleCanPlay);
    document.addEventListener('visibilitychange', handleVisibilityChange);
    document.addEventListener('touchstart', handleFirstInteraction, { once: true });
    document.addEventListener('click', handleFirstInteraction, { once: true });

    return () => {
      video.removeEventListener('canplay', handleCanPlay);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      document.removeEventListener('touchstart', handleFirstInteraction);
      document.removeEventListener('click', handleFirstInteraction);
    };
  }, []);

  const scrollToProducts = () => {
    document.getElementById('products')?.scrollIntoView({ behavior: 'smooth' });
  };

  const scrollToContact = () => {
    document.getElementById('contact')?.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <section className="hero" id="home">
      {/* Background Video */}
      <video
        ref={videoRef}
        autoPlay
        loop
        muted
        playsInline
        preload="auto"
        className="hero-video"
      >
        <source src="/video/header_video.mp4" type="video/mp4" />
      </video>

      {/* Overlay */}
      <div className="hero-overlay"></div>

      {/* Animated Background Elements */}
      <div className="hero-particles">
        <div className="particle"></div>
        <div className="particle"></div>
        <div className="particle"></div>
      </div>
      
      {/* Content */}
      <div className="hero-content">
        <h1 className="hero-title">
          <span className="hero-title-line">Intelligent Systems For</span>
          <span className="hero-title-accent">Next-Gen Underwater Operations</span>
        </h1>

        <p className="hero-description">
          Autonomous underwater vehicles and maritime monitoring systems
          for defense, security, and large-scale operations. Built for the future.
        </p>

        <div className="hero-cta">
          <button className="btn btn-primary" onClick={scrollToProducts}>
            <span>Explore Products</span>
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M5 12h14M12 5l7 7-7 7"/>
            </svg>
          </button>
          <button className="btn btn-secondary" onClick={scrollToContact}>
            <span>Contact Us</span>
          </button>
        </div>

        {/* Stats */}
        <div className="hero-stats">
          <div className="stat">
            <span className="stat-value">Subsea</span>
            <span className="stat-label">Drones</span>
          </div>
          <div className="stat-divider"></div>
          <div className="stat">
            <span className="stat-value">Sensor</span>
            <span className="stat-label">Systems</span>
          </div>
          <div className="stat-divider"></div>
          <div className="stat">
            <span className="stat-value">Maritime</span>
            <span className="stat-label">Intelligence</span>
          </div>
        </div>
      </div>

      {/* Logo Watermark */}
      <img src={logo} alt="SUBmerge Baltic" className="hero-logo-watermark" />
    </section>
  );
};

export default HeroSection;
